import { Divider, Drawer, Tag } from 'antd';
import { useEffect, useState } from 'react';
import type { ShiftPeriod, ShiftCellRecord, FilledShift } from '../types';
import ShiftManager from '../ShiftManager';
import { Optional } from '../../Shared';
import { isNullOrUndefined, compact } from '../../../lib/utils';
import { rootId } from '../lib/shift-service';
import { Notes } from '../../Notes';
import type { RelatedNoteWithCreatedAtAndUpdatedAtAsNumber } from '../../Notes/types';
import ShiftDetails from './ShiftDetails';
import { fullName } from '../lib/changeLog';
import { asDateNumber } from '../../../lib/utils/date-time';
import { noteDateEqualsShiftDate } from '../../Notes/util';
import type { NoteContent } from '../../../hooks/types';
import { ShiftNoteContent } from '../../Notes/ShiftNoteContent';
import { format } from 'date-fns';

export const shiftComponentTitleWidth = '140px';

/**
 * How shift notes should be formatted for saving in the database
 * Only for creating or updating notes without a changelog.
 *
 * @param shift - The shift that the note is being added to
 * @returns A function that takes a note and returns a string
 */
const formatOnSave =
  (shift: FilledShift) =>
  (note: string): NoteContent => ({
    content: note,
    date: format(new Date(shift.startTime), 'yyyy-MM-dd'),
  });

/**
 * Renders a drawer for a shift.
 * If the shift is readonly, it will render a readonly shift, otherwise it will render a shift manager.
 * This can be used to render multiple shifts side by side.
 */
const ShiftDrawer: React.FC<{
  /**
   * The shift to render
   */
  readonly shift: FilledShift;
  /**
   * Shift component will be readonly if cellRecord is undefined
   */
  readonly cellRecord: ShiftCellRecord | undefined;
  /**
   * The team role assignment name of this shift
   */
  readonly role: string;
  /**
   * Called when the drawer is closed
   */
  readonly onClose: () => void;
  /**
   * The default day time hours for this shift
   */
  readonly defaultDayTimeHours: ShiftPeriod;
  /**
   * Whether the drawer is open
   */
  readonly open: boolean;
  /**
   * The shift start time
   */
  readonly shiftStartTime: string;
}> = ({
  shift: providedShift,
  cellRecord,
  onClose,
  defaultDayTimeHours,
  open,
  role,
  shiftStartTime,
}) => {
  const [shift, setShift] = useState(providedShift);

  useEffect(() => {
    setShift(providedShift);
  }, [providedShift]);

  const allNotesToShow = shift.relatedNotes;

  const relatedNotesOnShiftStartTime: readonly RelatedNoteWithCreatedAtAndUpdatedAtAsNumber[] =
    allNotesToShow.filter(
      ({ date }) =>
        date !== undefined && noteDateEqualsShiftDate(date, shiftStartTime)
    );

  const relatedNotesNotOnShiftStartTime: readonly RelatedNoteWithCreatedAtAndUpdatedAtAsNumber[] =
    compact(
      allNotesToShow.filter(({ date }) =>
        date === undefined
          ? true
          : !noteDateEqualsShiftDate(date, shiftStartTime)
      )
    );

  return (
    <Drawer
      placement="right"
      closable={false}
      onClose={onClose}
      open={open}
      contentWrapperStyle={{ minWidth: '500px' }}
      destroyOnClose
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Optional show={shift.recordStatus === 'Deleted'}>
          <Tag
            color="red"
            style={{
              width: '100%',
              textAlign: 'center',
              padding: 5,
              marginBottom: 10,
            }}
          >
            This shift has been deleted
          </Tag>
        </Optional>

        {cellRecord !== undefined ? (
          <ShiftManager
            shift={shift}
            record={cellRecord}
            onFinish={onClose}
            defaultHours={defaultDayTimeHours}
            titleWidth={shiftComponentTitleWidth}
          />
        ) : !isNullOrUndefined(shift.startTime) &&
          !isNullOrUndefined(shift.endTime) ? (
          <ShiftDetails
            role={role}
            titleWidth={shiftComponentTitleWidth}
            employee={fullName(shift.user)}
            times={{
              start: asDateNumber(shift.startTime),
              end: asDateNumber(shift.endTime),
            }}
            status={shift.status}
            recurrence={{
              pattern: shift.recurrencePattern,
              endDate: shift.recurrenceEndDate,
            }}
          />
        ) : (
          <></>
        )}

        <Divider />

        <Notes
          notes={relatedNotesOnShiftStartTime}
          relatedNotes={relatedNotesNotOnShiftStartTime}
          primaryObjectId={rootId(shift)}
          readonly={cellRecord === undefined}
          formatOnSave={formatOnSave(shift)}
          render={(note) => (
            <ShiftNoteContent
              content={note.content}
              changes={note.metadata?.shift?.changes ?? []}
              date={
                note.date === undefined ||
                noteDateEqualsShiftDate(note.date, shiftStartTime)
                  ? undefined
                  : format(new Date(note.date), 'PPP')
              }
            />
          )}
        />
      </div>
    </Drawer>
  );
};

export default ShiftDrawer;
